import type { AccessLevel, SearchResult } from '@agentwiki/shared'
import { deriveEffectiveAuthorityState } from './source-authority-metadata'
import { formatMemoryClass, formatSourceType } from './document-kind'
import { getDocumentSourceIdentity } from './document-source-identity'

export type KnowledgeAuthority = 'active' | 'reference' | 'needs-review' | 'superseded' | 'expired'
type KnowledgeOwnership = 'manual' | 'source-backed' | 'unknown'

interface PresentationTerm<T extends string> {
  key: T
  label: string
  description: string
}

function term<T extends string>(key: T, label: string, description: string) {
  return { key, label, description } satisfies PresentationTerm<T>
}

export const KNOWLEDGE_PRESENTATION_TERMS = {
  ownership: {
    manual: term('manual', 'Manual document', 'Managed directly in AgentWiki'),
    'source-backed': term('source-backed', 'Source-backed', 'Managed by an external source'),
    unknown: term('unknown', 'Ownership unavailable', 'Ownership metadata was not returned'),
  },
  access: {
    private: term('private', 'Private access', 'Only explicitly authorized workspace members'),
    specific: term('specific', 'Specific access', 'Shared with specific people'),
    public: term('public', 'Public access', 'Available through public access'),
    unknown: term('unknown', 'Access unavailable', 'Access metadata was not returned'),
  },
  authority: {
    active: term('active', 'Active', 'Currently governs work'),
    reference: term('reference', 'Reference', 'Useful context that does not govern current work'),
    'needs-review': term('needs-review', 'Needs review', 'A human authority decision is required'),
    superseded: term('superseded', 'Superseded', 'Replaced by newer knowledge'),
    expired: term('expired', 'Expired', 'No longer current for its intended window'),
    unknown: term('unknown', 'Authority unavailable', 'Authority metadata was not returned'),
  },
  syncHealth: {
    verified: term('verified', 'Verified', 'Matches the last verified source revision'),
    stale: term('stale', 'Stale', 'Behind its source revision'),
    conflict: term('conflict', 'Sync conflict', 'Local and source snapshots differ'),
    'not-applicable': term(
      'not-applicable',
      'Not source-backed',
      'Manual documents do not have source sync health',
    ),
    unknown: term('unknown', 'Sync unknown', 'Source sync metadata was not returned'),
  },
} as const

interface PresentationSourceInput {
  sourceId?: string | null
  sourceType?: string | null
  sourcePath?: string | null
  repoUrl?: string | null
  syncStatus?: string | null
  memoryClass?: string | null
  lastSyncedAt?: number | null
  isStale?: boolean
  hasSyncConflict?: boolean
  authorityState?: KnowledgeAuthority | string | null
  effectiveAuthorityState?: KnowledgeAuthority | string | null
  authorityExpiresAt?: number | null
  authorityReviewedAt?: number | null
}

export interface DocumentResultPresentationInput {
  id: string
  title: string
  slug?: string
  description?: string | null
  summary?: string | null
  snippet?: string | null
  context?: string | null
  accessLevel?: AccessLevel | string | null
  authorityState?: KnowledgeAuthority | string | null
  effectiveAuthorityState?: KnowledgeAuthority | string | null
  authorityExpiresAt?: number | null
  authorityReviewedAt?: number | null
  sourceProvenance?: PresentationSourceInput | null
  ownershipState?: 'unknown'
}

function accessTerm(value: DocumentResultPresentationInput['accessLevel']) {
  return value === 'private' || value === 'specific' || value === 'public'
    ? KNOWLEDGE_PRESENTATION_TERMS.access[value]
    : KNOWLEDGE_PRESENTATION_TERMS.access.unknown
}

function authorityTerm(value: DocumentResultPresentationInput['authorityState']) {
  const terms = KNOWLEDGE_PRESENTATION_TERMS.authority
  return value && value in terms ? terms[value as KnowledgeAuthority] : terms.unknown
}

function isKnowledgeAuthority(value: unknown): value is KnowledgeAuthority {
  return (
    value === 'active' ||
    value === 'reference' ||
    value === 'needs-review' ||
    value === 'superseded' ||
    value === 'expired'
  )
}

function syncTerm(
  source: PresentationSourceInput | null | undefined,
  ownership: KnowledgeOwnership,
) {
  const terms = KNOWLEDGE_PRESENTATION_TERMS.syncHealth
  if (ownership === 'unknown') return terms.unknown
  if (!source) return terms['not-applicable']
  if (source.hasSyncConflict || source.syncStatus === 'conflict') return terms.conflict
  if (source.isStale || source.syncStatus === 'stale') return terms.stale
  if (source.syncStatus === 'synced') return terms.verified
  return terms.unknown
}

export function buildDocumentResultPresentation(input: DocumentResultPresentationInput) {
  const source = input.sourceProvenance
  const storedAuthority = input.authorityState ?? source?.authorityState
  const declaredEffectiveAuthority = input.effectiveAuthorityState ?? source?.effectiveAuthorityState
  const authorityExpiresAt = input.authorityExpiresAt ?? source?.authorityExpiresAt ?? null
  const effectiveAuthority = isKnowledgeAuthority(storedAuthority)
    ? deriveEffectiveAuthorityState(storedAuthority, authorityExpiresAt)
    : declaredEffectiveAuthority
  const ownership: KnowledgeOwnership = source
    ? 'source-backed'
    : input.ownershipState === 'unknown'
      ? 'unknown'
      : 'manual'
  const sourceIdentity =
    source?.sourceType && source.sourcePath
      ? getDocumentSourceIdentity({
          sourceType: source.sourceType,
          sourcePath: source.sourcePath,
          repoUrl: source.repoUrl ?? null,
        })
      : null

  return {
    kind: 'document' as const,
    id: input.id,
    title: input.title || 'Untitled',
    slug: input.slug ?? '',
    excerpt:
      input.summary ??
      input.description ??
      input.snippet ??
      input.context ??
      source?.sourcePath ??
      '',
    ownership: KNOWLEDGE_PRESENTATION_TERMS.ownership[ownership],
    access: accessTerm(input.accessLevel),
    authority: authorityTerm(effectiveAuthority),
    storedAuthority: authorityTerm(storedAuthority),
    authorityExpiresAt,
    authorityReviewedAt: input.authorityReviewedAt ?? source?.authorityReviewedAt ?? null,
    syncHealth: syncTerm(source, ownership),
    source: sourceIdentity
      ? { key: sourceIdentity.key, label: sourceIdentity.label }
      : source
        ? {
            key: `source:${source.sourceType ?? 'unknown'}`,
            label: source.sourceType ? formatSourceType(source.sourceType) : 'Source unavailable',
          }
        : ownership === 'manual'
          ? { key: 'manual', label: 'AgentWiki workspace' }
          : { key: 'unknown', label: 'Source unavailable' },
    memoryClass: source?.memoryClass ?? null,
    memoryClassLabel: source?.memoryClass ? formatMemoryClass(source.memoryClass) : null,
    lastSyncedAt: source?.lastSyncedAt ?? null,
  }
}

type SearchPresentationInput = SearchResult & {
  accessLevel?: AccessLevel | string | null
  authorityState?: KnowledgeAuthority | string | null
}

export function buildSearchResultPresentation(result: SearchPresentationInput) {
  if (result.resultType === 'upload') {
    return {
      kind: 'file' as const,
      id: result.id,
      title: result.title,
      excerpt: result.snippet ?? result.context ?? result.sourcePath ?? result.title,
      fileUrl: result.fileUrl ?? null,
      label: 'File result' as const,
    }
  }

  const isManual = result.sourceType === 'document'
  const hasSource = Boolean(result.sourceType && !isManual)
  return buildDocumentResultPresentation({
    ...result,
    ownershipState: !isManual && !hasSource ? 'unknown' : undefined,
    sourceProvenance: hasSource
      ? {
          sourceType: result.sourceType,
          sourcePath: result.sourcePath,
          repoUrl: result.repoUrl,
          syncStatus: result.syncStatus,
          memoryClass: result.memoryClass,
          lastSyncedAt: result.lastSyncedAt,
          isStale: result.isStale,
          hasSyncConflict: result.hasSyncConflict,
          sourceId: result.sourceId,
          authorityState: result.authorityState,
          effectiveAuthorityState: result.effectiveAuthorityState,
          authorityExpiresAt: result.authorityExpiresAt,
          authorityReviewedAt: result.authorityReviewedAt,
        }
      : null,
    authorityState: result.authorityState,
    effectiveAuthorityState: result.effectiveAuthorityState,
    authorityExpiresAt: result.authorityExpiresAt,
    authorityReviewedAt: result.authorityReviewedAt,
  })
}
